
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Pencil, Trash2, Check } from 'lucide-react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useBoardContext } from '@/contexts/BoardContext';
import { cn } from '@/lib/utils';

interface FormData {
  name: string;
  description: string;
  color: string;
}

interface TagManagementProps {
  onTagSelected?: (tagId: string) => void;
  selectionMode?: boolean;
  selectedTags?: string[];
}

export const TagManagement = ({ 
  onTagSelected,
  selectionMode = false,
  selectedTags = []
}: TagManagementProps) => {
  const { tagDefinitions, createTagDefinition, updateTagDefinition, deleteTagDefinition } = useBoardContext();
  const [editingTag, setEditingTag] = useState<string | null>(null);

  const createForm = useForm<FormData>({
    defaultValues: {
      name: '',
      description: '',
      color: '#3b82f6',
    },
  });

  const editForm = useForm<FormData>({
    defaultValues: {
      name: '',
      description: '',
      color: '#3b82f6',
    },
  });

  const onCreateSubmit = (data: FormData) => {
    createTagDefinition(data);
    createForm.reset();
    // Don't close dialog automatically in selection mode
  };

  const onEditSubmit = (data: FormData) => {
    if (editingTag) {
      updateTagDefinition({ tagId: editingTag, updates: data });
      setEditingTag(null);
      editForm.reset();
    }
  };

  const handleEdit = (tag: any) => {
    setEditingTag(tag.id);
    editForm.reset({
      name: tag.name,
      description: tag.description || '',
      color: tag.color,
    });
  };

  const handleDelete = (tagId: string) => {
    if (confirm('Are you sure you want to delete this tag?')) {
      deleteTagDefinition(tagId);
    }
  };

  const handleCancelEdit = () => {
    setEditingTag(null);
    editForm.reset();
  };

  const handleTagClick = (tagId: string) => {
    if (selectionMode && onTagSelected) {
      onTagSelected(tagId);
    }
  };

  const isTagSelected = (tagId: string) => {
    return selectedTags.includes(tagId);
  };

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-medium mb-2">Existing Tags</h4>
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {tagDefinitions?.map((tag) => (
            <div 
              key={tag.id} 
              className={cn(
                "flex items-center justify-between p-2 border rounded transition-colors",
                selectionMode && isTagSelected(tag.id) && "border-primary bg-primary/5"
              )}
            >
              {editingTag === tag.id ? (
                <Form {...editForm}>
                  <form onSubmit={editForm.handleSubmit(onEditSubmit)} className="flex-1 space-y-2">
                    <div className="flex gap-2">
                      <FormField
                        control={editForm.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              <Input {...field} placeholder="Tag name" className="h-8" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={editForm.control}
                        name="color"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                {...field}
                                type="color"
                                className="w-12 h-8 p-1"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={editForm.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input {...field} placeholder="Description (optional)" className="h-8" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="flex gap-2">
                      <Button type="submit" size="sm" className="h-7">
                        Save
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="h-7" onClick={handleCancelEdit}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                </Form>
              ) : (
                <>
                  <div className="flex items-center gap-2 flex-1">
                    {selectionMode && isTagSelected(tag.id) && (
                      <div className="flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground">
                        <Check className="h-3 w-3" />
                      </div>
                    )}
                    <Badge 
                      style={{ backgroundColor: tag.color, color: 'white' }}
                      className={cn(
                        selectionMode ? "cursor-pointer hover:opacity-80" : "",
                        selectionMode && isTagSelected(tag.id) && "ring-2 ring-primary ring-offset-2"
                      )}
                      onClick={() => handleTagClick(tag.id)}
                    >
                      {tag.name}
                    </Badge>
                    {tag.description && (
                      <span className="text-sm text-gray-600 dark:text-gray-400">{tag.description}</span>
                    )}
                    {selectionMode && isTagSelected(tag.id) && (
                      <span className="text-xs text-primary font-medium">Selected</span>
                    )}
                  </div>
                  {!selectionMode && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => handleEdit(tag)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        onClick={() => handleDelete(tag.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
          {(!tagDefinitions || tagDefinitions.length === 0) && (
            <p className="text-sm text-gray-500 dark:text-gray-400">No tags defined yet</p>
          )}
        </div>
      </div>

      <div>
        <h4 className="font-medium mb-2">Create New Tag</h4>
        <Form {...createForm}>
          <form onSubmit={createForm.handleSubmit(onCreateSubmit)} className="space-y-3">
            <FormField
              control={createForm.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Tag name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={createForm.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (optional)</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Tag description" rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={createForm.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <FormControl>
                    <div className="flex gap-2">
                      <Input
                        {...field}
                        type="color"
                        className="w-16 h-10"
                      />
                      <Input
                        {...field}
                        placeholder="#3b82f6"
                        className="flex-1"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full">
              {selectionMode ? 'Create and Select Tag' : 'Create Tag'}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
};
